import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  Container,
  ContainerContent,
  ContainerHeader,
  UserGroupList,
  UserProfile,
} from '@/component';
import UserInviteFormWithTrigger from '@/component/User/UserInviteFormWithTrigger';
import UserGroupFormWithTrigger from '@/component/UserGroup/UserGroupCreateForm';
import { USER_CONST } from '@/constant';
import {
  useUserCountQuery,
  useUserGroupListQuery,
  useUserGroupUserListQuery,
  useUserListQuery,
  useUserRemoveQuery,
  useUserRoleMapper,
} from '@/hook';
import {
  Remove,
  VDrawer,
  VFlex,
  VIconButton,
  VModal,
  VStack,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
} from '@/package';
import { useMainLayout } from '@/provider';
import { useAuthStore } from '@/store';
import type { User, UserGroup, UserRole } from '@/type';

const MemberListPage = () => {
  const [isMemberProfileOpen, setIsMemberProfileOpen] = useState(false);
  const [isUserRemoving, setIsUserRemoving] = useState(false);
  const [selectedGroupID, setSelectedGroupID] = useState<UserGroup['id']>(0);
  const [selectedGroupUserList, setSelectedGroupUserList] = useState<User[]>(
    [],
  );
  const [selectedUser, setSelectedUser] = useState<Nullish<User>>(null);

  const isUserGroupSelected = !!selectedGroupID;
  const { data: userList, isPending: isUserPending } = useUserListQuery({
    enabled: true,
  });
  const { data: userCount } = useUserCountQuery();
  const { data: groupUserList } = useUserGroupUserListQuery({
    enabled: isUserGroupSelected,
    userGroupID: selectedGroupID,
  });
  const { data: groupList, isPending: isGroupPending } =
    useUserGroupListQuery();
  const { mutateAsync: removeUser } = useUserRemoveQuery();

  const isRemovable = !!selectedGroupUserList.length;
  const isRemovableRole = useAuthStore.getState().isAdmin();
  const navigate = useNavigate();
  const { setTitle } = useMainLayout();
  const { mapUserRole } = useUserRoleMapper();

  const userNum = userList?.reduce(
    (acc, cur) => {
      return {
        ...acc,
        [cur.role]: acc[cur.role] + 1,
      };
    },
    {
      Admin: 0,
      Guest: 0,
      Manager: 0,
      SiteAdmin: 0,
      Worker: 0,
    },
  ) ?? {
    Admin: 0,
    Guest: 0,
    Manager: 0,
    SiteAdmin: 0,
    Worker: 0,
  };
  const numUserRoleList: { label: string; num: number; role: UserRole }[] = [
    {
      label: mapUserRole(USER_CONST.USER_ROLE.SITE_ADMIN),
      num: userNum.SiteAdmin,
      role: USER_CONST.USER_ROLE.SITE_ADMIN,
    },
    {
      label: mapUserRole(USER_CONST.USER_ROLE.MANAGER),
      num: userNum.Manager,
      role: USER_CONST.USER_ROLE.MANAGER,
    },
    {
      label: mapUserRole(USER_CONST.USER_ROLE.WORKER),
      num: userNum.Worker,
      role: USER_CONST.USER_ROLE.WORKER,
    },
    {
      label: mapUserRole(USER_CONST.USER_ROLE.GUEST),
      num: userNum.Guest,
      role: USER_CONST.USER_ROLE.GUEST,
    },
  ];
  const totalUser = numUserRoleList.reduce((acc, cur) => acc + cur.num, 0);

  const handleRemoveUserList = async () => {
    await Promise.allSettled(selectedGroupUserList.map(u => removeUser(u.id)));
  };

  const handleSelectGroup = (groupID: UserGroup['id']) => {
    setSelectedGroupID(groupID);
  };

  useEffect(() => {
    if (!selectedUser) return;

    const newUser = userList?.find(u => u.id === selectedUser.id);
    setSelectedUser(newUser);
  }, [userList]);

  useEffect(() => {
    setTitle('Members');
  }, []);

  if (isGroupPending || isUserPending) return <></>;

  return (
    <>
      <Container>
        <ContainerHeader>
          <VStack className="w-full pt-6 pb-2" gap={0.5}>
            {isRemovableRole && (
              <div className="w-fit">
                <VTab
                  defaultValue="0"
                  onValueChange={val => {
                    if (val === '1') navigate('/admin-setting');
                  }}
                >
                  <VTabTriggerContainer>
                    <VTabTrigger value="0">Members</VTabTrigger>
                    <VTabTrigger value="1">Admin settings</VTabTrigger>
                  </VTabTriggerContainer>
                </VTab>
              </div>
            )}
            <VFlex gap={1} items="center" className="w-full">
              <div className="text-headline-md font-semibold text-nowrap">
                All members
              </div>
              <VFlex justify="between" className="w-full" items="center">
                <VFlex className="px-2" gap={0.5} items="center">
                  <VFlex className="text-headline-sm text-blue-300" gap={0.25}>
                    <span>Total</span>
                    <span className="font-bold">{totalUser}</span>
                  </VFlex>
                  {numUserRoleList.map(({ label, num, role }) => {
                    return (
                      <VFlex
                        gap={0.5}
                        items="center"
                        key={role}
                        className="text-gray-500"
                      >
                        <div className="h-4 w-px bg-gray-300" />
                        <VFlex className="text-headline-sm" gap={0.25}>
                          <span>{label}</span>
                          <span className="font-bold">{num}</span>
                        </VFlex>
                      </VFlex>
                    );
                  })}
                </VFlex>
                <VFlex gap={0.5} items="center">
                  <UserGroupFormWithTrigger
                    key={Math.random()}
                    editOrCreate="create"
                    userGroupList={groupList ?? []}
                    userList={userList ?? []}
                  />
                  <UserInviteFormWithTrigger />
                  {isRemovableRole && (
                    <VIconButton
                      Icon={Remove}
                      disabled={!isRemovable}
                      size="lg"
                      variant="danger"
                      onClick={() => setIsUserRemoving(true)}
                    />
                  )}
                </VFlex>
              </VFlex>
            </VFlex>
          </VStack>
        </ContainerHeader>

        <ContainerContent>
          {groupList && (
            <UserGroupList
              groupList={groupList}
              onSelectGroup={handleSelectGroup}
              openMemberProfile={() => setIsMemberProfileOpen(true)}
              selectedGroupID={selectedGroupID}
              selectedGroupUserList={selectedGroupUserList}
              setSelectedGroupUserList={setSelectedGroupUserList}
              setSelectedUser={setSelectedUser}
              userCount={userCount ?? 0}
              userList={
                isUserGroupSelected ? (groupUserList ?? []) : (userList ?? [])
              }
            />
          )}
        </ContainerContent>
      </Container>

      <VDrawer
        dialogMainClass={'!bg-white'}
        position="right"
        size="xs"
        visible={isMemberProfileOpen}
        onUpdateVisible={setIsMemberProfileOpen}
      >
        <UserProfile
          closeMemberProfile={() => setIsMemberProfileOpen(false)}
          groupList={groupList}
          selectedUser={selectedUser}
        />
      </VDrawer>

      <VModal
        visible={isUserRemoving}
        title={() => (
          <div className="translate-y-1 text-body-xl text-error-500">
            Delete Project
          </div>
        )}
        size="small"
        footer={{
          cancelText: 'Cancel',
          confirmText: 'Delete',
          onConfirm: async () => {
            await handleRemoveUserList();
            setIsUserRemoving(false);
          },
        }}
        onUpdateVisible={visible => setIsUserRemoving(visible)}
      >
        <VFlex direction="col" className="gap-[10px]">
          <div className="text-body-xl text-black">
            Are you sure you want to remove the selected member?
          </div>

          <div className="w-full text-gray-500">
            This member will no longer have access to the workspace and all
            related permissions will be revoked.
            <br /> You can invite them again at any time.
          </div>
        </VFlex>
      </VModal>
    </>
  );
};

export default MemberListPage;
